import type { User } from "@/hooks/auth/use-user";
import { useFocus } from "@/hooks/use-focus";
import { supabase } from "@/lib/supabase";
import { PasswordInput } from "@/routes/auth/components/password-input";
import { PasswordSchema } from "@/utils/schema";
import { useToastMessage } from "@backpac/monica";
import {
  Button,
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Input,
} from "@backpac/monica";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { z } from "zod";

const EmailSignUpSchema = z
  .object({
    email: z.string().email("Please enter a valid email address."),
    password: PasswordSchema,
    confirmPassword: z.string(),
    name: z.string().min(1, "Name must not be empty.").max(20, "Please keep it under 20 characters."),
  })
  .refine((data) => data.password === data.confirmPassword, {
    message: "Your password do not match.",
    path: ["confirmPassword"],
  });

type EmailSignUpForm = z.infer<typeof EmailSignUpSchema>;

interface EmailSignUpFormProps {
  onSignupSuccess?: (user: User) => void;
  onSignInSuccess?: (user: User) => void;
  defaultValues?: { email?: string; name?: string };
  disabledEmail?: boolean;
  ctaText?: string;
}

export function EmailSignUpForm({
  onSignupSuccess,
  onSignInSuccess,
  defaultValues,
  disabledEmail,
  ctaText,
}: EmailSignUpFormProps) {
  const { error, success } = useToastMessage();
  const [passwordFieldRef] = useFocus<HTMLInputElement>();
  const [nameFieldRef, isNameFieldFocused] = useFocus<HTMLInputElement>();
  const form = useForm<EmailSignUpForm>({
    resolver: zodResolver(EmailSignUpSchema),
    defaultValues,
    mode: "onChange",
  });

  const onSubmit = async (data: EmailSignUpForm) => {
    const response = await supabase.auth.signUp({
      email: data.email,
      password: data.password,
      options: {
        data: { name: data.name },
      },
    });

    if (response.error) {
      error("Failed to sign up");
    } else if (response.data.user?.confirmed_at) {
      success("Sign up was successful!");

      const signInResponse = await supabase.auth.signInWithPassword({
        email: data.email,
        password: data.password,
      });

      if (signInResponse.error) {
        error("Failed to sign in");
      } else {
        onSignInSuccess?.(signInResponse.data.user);
      }
    } else {
      if (response.data.user != null) {
        onSignupSuccess?.(response.data.user);
      }
    }
  };

  return (
    <Form {...form}>
      <form id="email-sign-up" className="flex flex-col w-full gap-4" onSubmit={form.handleSubmit(onSubmit)}>
        <FormField
          control={form.control}
          name="email"
          render={({ field }) => (
            <FormItem>
              <FormLabel className="flex items-center gap-1.5">Email</FormLabel>
              <FormControl>
                <Input {...field} type="email" placeholder="Your email address" disabled={disabledEmail} />
              </FormControl>
              <FormMessage color="colorRed" />
            </FormItem>
          )}
        />

        <div className="flex flex-col gap-2">
          <FormField
            control={form.control}
            name="password"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="flex items-center gap-1.5">Password</FormLabel>
                <FormControl>
                  <PasswordInput {...field} ref={passwordFieldRef} />
                </FormControl>
                <FormMessage color="colorRed" />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="confirmPassword"
            render={({ field }) => (
              <FormItem>
                <FormControl>
                  <PasswordInput {...field} placeholder="Confirm password" />
                </FormControl>
                {/* TODO: Use form.formState.errors. Current implementation is not working. */}
                {form.getValues("password") !== form.getValues("confirmPassword") && (
                  <FormDescription className="text-colorRed">Password do not match.</FormDescription>
                )}
              </FormItem>
            )}
          />
        </div>

        <FormField
          control={form.control}
          name="name"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Name</FormLabel>
              <FormControl>
                <Input {...field} ref={nameFieldRef} type="text" placeholder="Your name" maxLength={20} />
              </FormControl>
              <FormMessage />
              {isNameFieldFocused ? (
                <FormDescription>Enter up to 20 characters ({form.getValues("name")?.length ?? 0}/20)</FormDescription>
              ) : null}
            </FormItem>
          )}
        />
      </form>
      <Button
        form="email-sign-up"
        type="submit"
        variant="black"
        size="lg"
        className="w-full"
        disabled={form.formState.isSubmitting || form.formState.isValid === false}
      >
        {ctaText ?? "Sign up"}
      </Button>
    </Form>
  );
}
